import React, { useState, FormEvent } from 'react';
import authService from '../services/authService';
import './Login.css';
import './Pages.css';

interface LoginProps {
  onLogin: (token: string, user: any, restaurants?: any[]) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [session, setSession] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Remove formatação do telefone (mantém apenas números)
      const cleanPhone = phone.replace(/\D/g, '');
      const phoneWithCountryCode = `+55${cleanPhone}`;

      const response = await authService.login({
        phone: phoneWithCountryCode,
        password
      });

      // Verificar se precisa trocar senha
      if (response.requiresNewPassword && response.session) {
        setSession(response.session);
        setTemporaryPassword(password);
        setShowChangePassword(true);
        setLoading(false);
        return;
      }

      onLogin(response.token, response.user, response.restaurants);
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Erro ao fazer login. Verifique suas credenciais.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validações
    if (newPassword.length < 8) {
      setError('A nova senha deve ter no mínimo 8 caracteres');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError('As senhas não coincidem');
      return;
    }

    setChangingPassword(true);

    try {
      const cleanPhone = phone.replace(/\D/g, '');
      const phoneWithCountryCode = `+55${cleanPhone}`;

      const response = await authService.changePassword({
        phone: phoneWithCountryCode,
        temporaryPassword: temporaryPassword,
        newPassword: newPassword,
        session: session
      });

      onLogin(response.token, response.user, response.restaurants);
    } catch (err: any) {
      console.error('Change password error:', err);
      setError(err.response?.data?.message || 'Erro ao trocar senha. Tente novamente.');
    } finally {
      setChangingPassword(false);
    }
  };

  const formatPhone = (value: string) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Formata: (XX) XXXXX-XXXX
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">
            <span className="login-logo-icon">✓</span>
            <span className="login-logo-text">FidHub Admin</span>
          </div>
          <h1>Bem-vindo de volta</h1>
          <p>Entre com suas credenciais para acessar o painel</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="phone">Número de Telefone</label>
            <div className="phone-input-group">
              <span className="phone-prefix">+55</span>
              <input
                id="phone"
                type="tel"
                placeholder="(11) 98765-4321"
                value={phone}
                onChange={handlePhoneChange}
                maxLength={15}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Senha</label>
            <input
              id="password"
              type="password"
              placeholder="Digite sua senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner"></span>
                Entrando...
              </>
            ) : (
              'Entrar'
            )}
          </button>

          <div className="login-footer">
            <a href="#forgot" className="forgot-link">Esqueceu sua senha?</a>
            <div className="register-prompt">
              Ainda não tem uma conta?{' '}
              <a href="#register" className="register-link">Criar conta</a>
            </div>
          </div>
        </form>

        {/* Modal de troca de senha */}
        {showChangePassword && (
          <div className="modal-overlay" onClick={(e) => e.stopPropagation()}>
            <div className="login-container" style={{
              position: 'relative',
              maxWidth: '400px',
              width: '90%',
              margin: 'auto'
            }} onClick={(e) => e.stopPropagation()}>
              <div className="login-header">
                <h1>Definir Nova Senha</h1>
                <p>Como esta é sua primeira vez, você precisa definir uma nova senha</p>
              </div>

              <form onSubmit={handleChangePassword} className="login-form">
                {error && (
                  <div className="login-error">
                    <span>⚠️</span>
                    <span>{error}</span>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="newPassword">Nova Senha</label>
                  <input
                    id="newPassword"
                    type="password"
                    placeholder="Mínimo 8 caracteres"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={changingPassword}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmNewPassword">Confirmar Nova Senha</label>
                  <input
                    id="confirmNewPassword"
                    type="password"
                    placeholder="Digite a nova senha novamente"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    minLength={8}
                    disabled={changingPassword}
                  />
                </div>

                <button type="submit" className="login-button" disabled={changingPassword}>
                  {changingPassword ? (
                    <>
                      <span className="spinner"></span>
                      Salvando...
                    </>
                  ) : (
                    'Salvar Nova Senha'
                  )}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;

